import { useState } from 'react';

import { cn } from '@foundation/utils/cn';

interface IYouTubeVideoEmbedProps {
  readonly url: string;
  readonly title?: string;
  readonly onPlay?: () => void;
  readonly className?: string;
}

function extractYouTubeId(url: string): string | null {
  const match =
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/.exec(
      url,
    );
  return match?.[1] ?? null;
}

const EMBED_PARAMS = new URLSearchParams({
  autoplay: '1',
  controls: '1',
  modestbranding: '1', // hide the YouTube logo in the control bar
  rel: '0', // only show related videos from the same channel
  showinfo: '0',
  iv_load_policy: '3', // hide video annotations
});

export function YouTubeVideoEmbed({
  url,
  title = 'Video',
  onPlay,
  className,
}: IYouTubeVideoEmbedProps) {
  const [isPlaying, setIsPlaying] = useState(false);

  const videoId = extractYouTubeId(url);

  if (!videoId) {
    return null;
  }

  const handlePlay = () => {
    setIsPlaying(true);
    onPlay?.();
  };

  return (
    <div
      className={cn(
        'relative w-full aspect-video overflow-hidden rounded-md bg-black',
        className,
      )}
    >
      {isPlaying ? (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${videoId}?${EMBED_PARAMS.toString()}`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
          className="absolute inset-0 h-full w-full"
        />
      ) : (
        <>
          <img
            src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
            alt={title}
            className="h-full w-full object-cover"
            onError={(e) => {
              e.currentTarget.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
            }}
          />
          <button
            type="button"
            onClick={handlePlay}
            className="absolute inset-0 z-10 flex items-center justify-center"
            aria-label="Play video"
          >
            <svg
              className="h-20 w-20 [filter:drop-shadow(0_2px_8px_rgba(0,0,0,0.6))]"
              viewBox="0 0 87 87"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M80.2394 39.634C82.301 40.8242 83.3317 41.4194 83.6777 42.1963C83.9794 42.874 83.9794 43.6479 83.6777 44.3256C83.3317 45.1026 82.301 45.6977 80.2394 46.8879L27.9121 77.0991C25.8505 78.2893 24.8198 78.8845 23.9739 78.7956C23.2362 78.718 22.566 78.3311 22.1299 77.7309C21.63 77.0429 21.63 75.8526 21.63 73.4722L21.63 13.0498C21.63 10.6693 21.63 9.47907 22.1299 8.79101C22.566 8.19086 23.2362 7.80391 23.974 7.72637C24.8198 7.63747 25.8506 8.23259 27.9121 9.42282L80.2394 39.634Z"
                fill="white"
              />
            </svg>
          </button>
        </>
      )}
    </div>
  );
}
